import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { IconDownload } from '@tabler/icons-react'
import { FilterForm } from '@/components/filters/FilterForm'

import { Button } from '@/components/buttons/Button'
import { PageHeader } from '@/components/headers/PageHeader'
import { fetchScorecardsByOrg } from '@/reducers/scorecards/scorecards.actions'
import { ModalActions } from '@/components/modals/ModalActions'
import { FormField } from '@/components/forms/FormField'

import Select from '../../components/forms/Select'
import {
  fetchOrganizationAndLeaderboards,
  fetchLeaderboard,
  createEditLeaderboard,
  deleteLeaderboard,
  exportLeaderboardToCsv,
} from '../../reducers/leaderboards/leaderboards.actions'
import { getLeaderboardBody } from './helpers'
import { ButtonAndFormModal } from '../../components/layout/modals/ButtonAndFormModal'
import { LeaderboardForm } from './LeaderboardForm/LeaderboardForm'

export const LeaderboardFilters = () => {
  const dispatch = useDispatch()
  const { organizationid: organizationId, edit_leaderboard: canEditLeaderboard } = useSelector(
    (state) => state.currentUser
  )
  const { data, filters, loading } = useSelector((state) => state.leaderboards)
  const organizations = useSelector((state) => state.organizations)
  const selectedLeaderboard = data.leaderboards.find(
    (item) => item?.leaderboard?.id === filters.leaderboardId
  )
  const isBaltoAdmin = organizationId === 1

  const handleSelectOrganization = (option) => {
    const selectedOrganizationId = isBaltoAdmin ? option.value : organizationId
    dispatch(fetchOrganizationAndLeaderboards(selectedOrganizationId))
    dispatch(fetchScorecardsByOrg(selectedOrganizationId))
  }

  const handleSelectLeaderboard = (option) => {
    const leaderboardId = option.value

    dispatch(fetchLeaderboard(filters.organizationId, leaderboardId))
  }

  const handleCreateEditLeaderboard = (values, leaderboard) => {
    // if leaderboard is passed, it's an edit
    const leaderboardId = leaderboard?.leaderboard?.id ? leaderboard.leaderboard.id : null
    const orgId = filters.organizationId
    const body = getLeaderboardBody(values, leaderboardId)

    // Resolves false when the save failed, so the form can stay open.
    return dispatch(createEditLeaderboard(orgId, body, leaderboardId))
  }

  const handleDeleteLeaderboard = (leaderboardId) => {
    dispatch(deleteLeaderboard(filters.organizationId, leaderboardId))
  }

  useEffect(() => {
    if (!isBaltoAdmin) {
      handleSelectOrganization(organizationId)
    }
  }, [])

  return (
    <>
      <PageHeader
        title="Leaderboards"
        actions={
          canEditLeaderboard && (
            <ButtonAndFormModal
              buttonLabel="Create Leaderboard"
              modalTitle="Create Leaderboard"
              modalId="create-leaderboard"
              disabled={isBaltoAdmin && !filters.organizationId}
              buttonProps={{ primary: true }}
              form={<LeaderboardForm handleSubmit={handleCreateEditLeaderboard} />}
            />
          )
        }
      />

      <FilterForm small data-testid="leaderboard-filters">
        {isBaltoAdmin && (
          <FormField>
            <label>Organization</label>
            <div data-testid="organization-dropdown">
              <Select
                isClearable={false}
                placeholder="Select Organization"
                options={organizations.map((org) => ({
                  label: org.name,
                  value: org.id,
                }))}
                loading={loading.organizations}
                value={filters.organizationId}
                onChange={(option) => handleSelectOrganization(option)}
              />
            </div>
          </FormField>
        )}

        <FormField>
          <label>Leaderboard</label>
          <div data-testid="leaderboard-dropdown">
            <Select
              isClearable={false}
              placeholder="Select Leaderboard"
              options={data.leaderboards.map((item) => ({
                label: item.version.name,
                value: item.leaderboard.id,
              }))}
              loading={loading.leaderboards}
              value={filters.leaderboardId}
              onChange={(option) => handleSelectLeaderboard(option)}
            />
          </div>
        </FormField>

        {canEditLeaderboard && (
          <FormField>
            <label>Leaderboard Settings</label>

            <div className="flex-align-center small-gap">
              <ButtonAndFormModal
                buttonLabel="Edit"
                modalTitle="Edit Leaderboard"
                modalId="edit-leaderboard"
                disabled={!filters.leaderboardId}
                buttonProps={{ primary: true }}
                form={
                  <LeaderboardForm
                    isEdit
                    selectedLeaderboard={selectedLeaderboard}
                    handleSubmit={handleCreateEditLeaderboard}
                  />
                }
              />

              <ButtonAndFormModal
                buttonLabel="Delete"
                modalTitle="Delete Leaderboard"
                modalId="delete-leaderboard"
                disabled={!filters.leaderboardId}
                modalProps={{ size: 'tiny' }}
                buttonProps={{ critical: true }}
                form={
                  <ModalActions
                    onSubmit={() => handleDeleteLeaderboard(filters.leaderboardId)}
                    isDelete
                  >
                    <p>Are you sure you want to delete this leaderboard?</p>
                  </ModalActions>
                }
              />

              <Button
                data-testid="export-button"
                disabled={!filters.leaderboardId}
                loading={loading.csv}
                onClick={() => dispatch(exportLeaderboardToCsv(data, filters.leaderboardId))}
              >
                <IconDownload />
                Export
              </Button>
            </div>
          </FormField>
        )}
      </FilterForm>
    </>
  )
}
